import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CitySelectModal from "../CitySelectModal/CitySelectModal";
import { useCities } from "../../context/CitiesContext";
import {
  getLastCitySlug,
  setLastCitySlug,
  hasCompletedCityOnboarding,
  setCityOnboardingDone,
  DEFAULT_CITY,
  slugToDisplayName,
} from "../../utils/userPreferences";
import { getPathAfterCityOnboarding } from "../../utils/homeRouteFromPreferences";
import { trackEvent } from "../../utils/analytics";

/**
 * First-visit city picker (BookMyShow-style).
 *
 * Rules:
 * - Do not show if `proppins_city_onboarding_done` is set.
 * - Do not show if URL has `?city=` (we persist slug and mark done).
 * - Do not show if `proppins_last_city_slug` exists (returning users / migration).
 * - Otherwise block until the user picks a city, uses the default shortcut, or we’d otherwise
 *   leave them without a city (handled by modal only).
 */
export default function CityOnboardingGate() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cities, loading: citiesLoading } = useCities();
  const [phase, setPhase] = useState("checking"); // checking | show | done

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (hasCompletedCityOnboarding()) {
      setPhase("done");
      return;
    }

    const qs = new URLSearchParams(location.search || "");
    const urlCity = qs.get("city")?.trim();
    if (urlCity) {
      setLastCitySlug(urlCity);
      setCityOnboardingDone();
      setPhase("done");
      return;
    }

    if (getLastCitySlug()) {
      setCityOnboardingDone();
      setPhase("done");
      return;
    }

    setPhase("show");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (phase === "show") {
      trackEvent("city_onboarding_shown", { path: location.pathname });
    }
  }, [phase, location.pathname]);

  const finalizeCity = useCallback(
    (city, source) => {
      const name = city?.name || "";
      const slug =
        city?.slug ||
        String(name)
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-") ||
        DEFAULT_CITY;
      setLastCitySlug(slug);
      setCityOnboardingDone();
      const path = getPathAfterCityOnboarding(location.pathname, location.search, slug);
      navigate(path, { replace: true });
      trackEvent("city_onboarding_complete", { city_slug: slug, source });
      setPhase("done");
    },
    [location.pathname, location.search, navigate]
  );

  const handleSelectCity = useCallback(
    (city) => {
      finalizeCity(city, "list");
    },
    [finalizeCity]
  );

  const handleContinueWithDefault = useCallback(() => {
    const slug = DEFAULT_CITY;
    const match = cities.find((c) => (c.slug || "").toLowerCase() === slug);
    const city = match || { name: slugToDisplayName(slug), slug };
    finalizeCity(city, "default_shortcut");
  }, [cities, finalizeCity]);

  if (phase !== "show") return null;

  return (
    <CitySelectModal
      show
      allowDismiss={false}
      showTitle
      showPrivacyHint
      continueWithDefaultLabel={`Continue with ${slugToDisplayName(DEFAULT_CITY)}`}
      onContinueWithDefault={handleContinueWithDefault}
      cities={cities}
      loading={citiesLoading}
      selectedSlug=""
      onClose={() => {}}
      onSelectCity={handleSelectCity}
    />
  );
}
